/* eslint-disable react/no-unused-prop-types */
import { ReactNode, useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Field } from 'react-final-form';
import { Button, Modal, RenderIf } from 'react-rainbow-components';
import { Pencil } from 'lucide-react';
import { useReduxForm } from '@rainbow-modules/hooks';
import { showAppNotification } from '@rainbow-modules/app';
import UniversalForm from 'components/UniversalForm';
import ModalBody from 'components/ModalBody';
import MicrophoneIcon from 'components/icons/microphone';
import DictateForm from 'components/SendNoteForm/DictateForm';
import useMultipartMutation from 'data/hooks/useMultipartMutation';
import { INVOICE_CHANGE_REASON_MAX_LENGTH } from '../constants';
import {
    ActionButton,
    ActionsContainer,
    ButtonsContainer,
    DictateFormContainer,
    FieldContainer,
    FieldLabel,
    InputWrapper,
    ModalContent,
    ReasonField,
    RequiredMark,
} from './styled';

const FORM_ID = 'edit-invoice-change-reason-form';

const buttonStyles = {
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    marginLeft: '0.7rem',
};

const validateChangeReason = (value: unknown) => {
    const reason = String(value || '').trim();
    if (!reason) return 'A reason is required.';
    if (reason.length > INVOICE_CHANGE_REASON_MAX_LENGTH) {
        return `Reason must be ${INVOICE_CHANGE_REASON_MAX_LENGTH} characters or fewer.`;
    }
    return undefined;
};

interface ChangeReasonInputProps {
    id?: string;
    className?: string;
    name?: string;
    label?: ReactNode;
    placeholder?: string;
    error?: ReactNode;
    required?: boolean;
    disabled?: boolean;
    value?: string;
    onChange?: (value: string) => void;
}

const ChangeReasonInput = (props: ChangeReasonInputProps) => {
    const { agentId = '' } = useParams();
    const {
        value: valueInProps = '',
        onChange = () => {},
        error: errorInProps,
        label: labelInProps,
        placeholder: placeholderInProps,
        required: requiredInProps,
        disabled: disabledInProps,
        id: idInProps,
        className: classNameInProps,
        name: nameInProps,
    } = useReduxForm(props);
    const [isDictating, setIsDictating] = useState(false);
    const {
        isLoading: isTranscribing,
        mutateAsync: transcribeAudio,
    } = useMultipartMutation<FormData, { transcription?: string }>({
        method: 'POST',
        pathname: `/agents/${agentId}/transcribe`,
    });
    const disableElements = isTranscribing || disabledInProps;

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            onChange(event.target.value);
        },
        [onChange],
    );

    const handleDictateAccept = useCallback(async (blob: Blob | null) => {
        setIsDictating(false);
        if (!blob) return;

        try {
            const formData = new FormData();
            formData.append('audio', blob);
            const { transcription } = await transcribeAudio({
                body: formData,
            });
            if (transcription) {
                onChange(transcription);
            }
        } catch (err) {
            const message = (err as Error)?.message || 'Something went wrong. Please try again.';
            showAppNotification({
                title: 'Error',
                description: message,
                icon: 'error',
                timeout: 5000,
            });
        }
    }, [onChange, transcribeAudio]);

    if (isDictating) {
        return (
            <FieldContainer className={classNameInProps}>
                <RenderIf isTrue={Boolean(labelInProps)}>
                    <FieldLabel>
                        {labelInProps}
                        <RenderIf isTrue={requiredInProps}>
                            <RequiredMark title="required">*</RequiredMark>
                        </RenderIf>
                    </FieldLabel>
                </RenderIf>
                <DictateFormContainer>
                    <DictateForm
                        onAccept={handleDictateAccept}
                        onCancel={() => setIsDictating(false)}
                    />
                </DictateFormContainer>
            </FieldContainer>
        );
    }

    return (
        <FieldContainer className={classNameInProps}>
            <RenderIf isTrue={Boolean(labelInProps)}>
                <FieldLabel>
                    {labelInProps}
                    <RenderIf isTrue={requiredInProps}>
                        <RequiredMark title="required">*</RequiredMark>
                    </RenderIf>
                </FieldLabel>
            </RenderIf>
            <InputWrapper>
                <ReasonField
                    id={idInProps}
                    name={nameInProps}
                    placeholder={placeholderInProps}
                    disabled={disableElements}
                    required={requiredInProps}
                    error={errorInProps}
                    value={valueInProps}
                    onChange={handleChange}
                    maxLength={INVOICE_CHANGE_REASON_MAX_LENGTH}
                    rows={3}
                />
                <ActionsContainer>
                    <ActionButton
                        tooltip="Dictate"
                        icon={<MicrophoneIcon />}
                        onClick={() => setIsDictating(true)}
                        disabled={disableElements}
                    />
                </ActionsContainer>
            </InputWrapper>
        </FieldContainer>
    );
};

interface ChangeReasonModalProps {
    isOpen?: boolean;
    isSubmitting?: boolean;
    onRequestClose: () => void;
    onConfirm: (reason: string) => void;
}

const ChangeReasonModal = ({
    isOpen,
    isSubmitting,
    onRequestClose,
    onConfirm,
}: ChangeReasonModalProps) => (
    <Modal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        title=""
        footer={(
            <ButtonsContainer>
                <Button
                    // eslint-disable-next-line react/forbid-component-props
                    style={buttonStyles}
                    label="Cancel"
                    variant="border"
                    borderRadius="semi-square"
                    disabled={isSubmitting}
                    onClick={onRequestClose}
                />
                <Button
                    // eslint-disable-next-line react/forbid-component-props
                    style={buttonStyles}
                    label="Save changes"
                    variant="brand"
                    type="submit"
                    form={FORM_ID}
                    borderRadius="semi-square"
                    disabled={isSubmitting}
                />
            </ButtonsContainer>
        )}
    >
        <UniversalForm
            id={FORM_ID}
            key={isOpen ? 'open' : 'closed'}
            onSubmit={(values: Record<string, unknown>) => {
                onConfirm(String(values.changeReason || '').trim());
            }}
        >
            <ModalContent>
                <ModalBody
                    icon={<Pencil />}
                    title="Reason for this change"
                    description="This invoice is not a draft. A reason is required before the change is saved."
                />
                <Field
                    name="changeReason"
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    component={ChangeReasonInput}
                    label="Change Reason"
                    placeholder="Why is this invoice being changed?"
                    required
                    disabled={isSubmitting}
                    validate={validateChangeReason}
                />
            </ModalContent>
        </UniversalForm>
    </Modal>
);

export default ChangeReasonModal;
